const MAX_NUMBERS_TO_RESOLVE = 3;
const RESOLVE_LISTING_LIMIT = 50;

type ResolvePhoneSearchOptions = {
  // Firestore `in` cap for the caller's query (lower when the query also has an `or(...)`)
  maxValues?: number;
  // only resolve complete, valid numbers; skip the partial digit match (e.g. duplicate-case check)
  exactNumbersOnly?: boolean;
};

/**
 * Firestore only matches a phone field as a whole string, so a number that is one of several
 * ("+60102223333; +60169998888") or a partial number can't be found with `==`. This resolves the
 * typed input to the stored values that contain it through the Postgres `customer.contact_number`
 * mirror (`GET /api/listing/customer-contact-num`), and returns a Firestore condition over them.
 * See docs/PHONE_NUMBER_FORMAT.md.
 */
export const usePhoneSearch = () => {
  const { getCustomerContactNumListing } = useNetwork();

  const fetchStoredContactNumbers = async (query: string) => {
    const response = await getCustomerContactNumListing({
      query,
      limit: RESOLVE_LISTING_LIMIT,
    });
    return response.data.map((item) => item.contactNumber).filter(Boolean);
  };

  const findStoredValuesContainingPhones = async (searchPhones: string[]) => {
    const results = await Promise.all(
      searchPhones
        .slice(0, MAX_NUMBERS_TO_RESOLVE)
        .map((phone) => fetchStoredContactNumbers(phone)),
    );
    return filterPhoneListsContaining(searchPhones, results.flat());
  };

  const resolvePhoneSearchCondition = async (
    input: string,
    options: ResolvePhoneSearchOptions = {},
  ): Promise<PhoneSearchCondition> => {
    const { maxValues = PHONE_SEARCH_MAX_VALUES, exactNumbersOnly = false } =
      options;
    const searchPhones = parsePhoneList(input).phones;
    const inputDigits = input.replace(/\D/g, "");

    try {
      if (searchPhones.length) {
        const matched = await findStoredValuesContainingPhones(searchPhones);
        return phoneSearchCondition(input, matched, maxValues);
      }

      if (
        !exactNumbersOnly &&
        inputDigits.length >= PHONE_SEARCH_MIN_PARTIAL_DIGITS
      ) {
        const matched = await fetchStoredContactNumbers(input.trim());
        return phoneSearchCondition(input, matched, maxValues);
      }
    } catch (err) {
      console.error("Failed to resolve phone search values", err);
    }

    return phoneSearchCondition(input, [], maxValues);
  };

  return { resolvePhoneSearchCondition };
};
